import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteClient, listClients, runAllForClient } from '../lib/api';
import type { RunAllStep } from '../lib/api';
import type { Client } from '../types';
import { useAuth } from '../lib/auth';
import { GBIBtn, T, sharedStyles } from './Shared';
import { useIsMobile } from '../lib/useIsMobile';

interface NavHeaderProps {
  currentClient: Client;
  onReloadBundle?: () => void;
  isClientRole?: boolean;
}

// Sticky top bar on the client detail page. Provides:
//   • Back to /admin (← All Clients)
//   • Client switcher dropdown (loads the full list, jumps to /admin/clients/:id)
//   • Update client data button (runs the 4-step pipeline; shows a progress overlay)
// Lives ABOVE the existing ClientDashboard so the dashboard's internal layout
// stays untouched.
export function NavHeader({ currentClient, onReloadBundle, isClientRole }: NavHeaderProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState<Client[] | null>(null);
  const [filter, setFilter] = useState('');
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Run-All state
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<RunAllStep[] | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Lazy-load the full client list the first time the dropdown opens.
  useEffect(() => {
    if (!open || clients !== null) return;
    listClients()
      .then(setClients)
      .catch(() => setClients([]));
  }, [open, clients]);

  // Click outside to close.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleRunAll = async () => {
    if (running) return;
    if (!confirm('Update this client data now? This refreshes Google Places, pulls completed Maps results, updates stale AI visibility, and regenerates Insights. Provider calls may incur charges.')) return;
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const res = await runAllForClient(currentClient.id);
      setRunResult(res.steps);
      onReloadBundle?.();
    } catch (err) {
      setRunError(err instanceof Error ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    const typed = window.prompt(
      `This permanently deletes the client "${currentClient.name}" along with ALL its place data, Maps scans, AI scans, AI keywords and insights.\n\nType the client name to confirm:`,
    );
    if (typed === null) return;
    if (typed.trim() !== currentClient.name) {
      alert('Client name did not match — delete cancelled.');
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteClient(currentClient.id);
      // Navigate home; the cards list reloads on its own.
      navigate('/admin');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
      setDeleting(false);
    }
  };

  const all = clients ?? [];
  // The business head of the currently-open client (its own id if it IS a head).
  const headId = currentClient.parentClientId ?? currentClient.id;

  // All locations of this business: the head + every branch pointing at it.
  const siblings = all
    .filter(c => c.id === headId || c.parentClientId === headId)
    .filter(c =>
      !filter.trim() ||
      c.name.toLowerCase().includes(filter.toLowerCase()) ||
      (c.locationLabel ?? '').toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => (a.id === headId ? -1 : b.id === headId ? 1 : a.name.localeCompare(b.name)));

  // Other businesses: show only their heads (one entry per business) so the
  // list stays short. Clicking a head opens that business.
  const others = all
    .filter(c => c.id !== headId && c.parentClientId !== headId)
    .filter(c => !c.parentClientId) // heads only
    .filter(c =>
      !filter.trim() ||
      c.name.toLowerCase().includes(filter.toLowerCase()));

  // Business head client (for the title) + whether this is a multi-location biz.
  const headClient = all.find(c => c.id === headId) ?? currentClient;
  const isMultiLocation = siblings.length > 1;
  const currentLocLabel = currentClient.locationLabel?.trim() || currentClient.location;

  return (
    <>
      {/* On a phone this admin bar is NOT sticky. The dashboard below has its
          own sticky bar (hamburger + client name) at top:0 with a higher
          z-index, so two stuck bars fought for the same strip and only this
          one's wrapped second row stayed visible. Here it scrolls away and
          the dashboard bar owns the top of the screen. */}
      <div style={{
        position: isMobile ? 'relative' : 'sticky', top: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 8,
        padding: isMobile ? '8px 14px' : '10px 20px',
        background: '#fff',
        borderBottom: `1px solid ${T.border}`,
        boxShadow: '0 1px 0 rgba(0,0,0,0.02)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: '1 1 auto' }}>
          {!isClientRole && (
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'transparent', border: `1px solid ${T.border}`,
                borderRadius: 6, padding: isMobile ? '6px 9px' : '6px 12px', cursor: 'pointer',
                fontSize: 12, color: T.fgSecondary, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
              title="Back to all clients"
              aria-label="Back to all clients"
            >
              {isMobile ? '←' : '← All Clients'}
            </button>
          )}

          {/* Client switcher */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setOpen(o => !o)}
              style={{
                background: T.bgSubtle, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: isMobile ? '6px 9px' : '6px 12px', cursor: 'pointer',
                fontSize: 12, color: T.fgPrimary, fontFamily: sharedStyles.fontDisplay, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                maxWidth: isMobile ? '100%' : 320, minWidth: 0,
              }}
            >
              {isMultiLocation && (
                <span style={{
                  fontSize: 9, fontWeight: 700, color: T.cyan,
                  background: 'rgba(18,181,196,0.10)', border: `1px solid ${T.cyan}`,
                  borderRadius: 8, padding: '1px 6px', flexShrink: 0,
                }}>📍 {siblings.length}</span>
              )}
              <span style={{
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: isMobile ? '100%' : 240, minWidth: 0,
                display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2,
              }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {isMultiLocation ? headClient.name : currentClient.name}
                </span>
                <span style={{ fontSize: 9, fontWeight: 400, color: T.fgMuted }}>
                  📍 {isMultiLocation ? currentLocLabel : currentClient.location}
                </span>
              </span>
              <span style={{ fontSize: 9, color: T.fgMuted }}>▼</span>
            </button>
            {open && (
              <div style={{
                position: 'absolute', top: '110%', left: 0,
                background: '#fff', border: `1px solid ${T.border}`,
                borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                width: isMobile ? 'min(320px, calc(100vw - 28px))' : 320,
                maxHeight: 420, overflowY: 'auto',
                padding: 6,
              }}>
                <input
                  autoFocus
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="Search clients…"
                  style={{
                    width: '100%', padding: '8px 10px', boxSizing: 'border-box',
                    fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 4,
                    marginBottom: 4, outline: 'none',
                  }}
                />
                {clients === null && (
                  <div style={{ padding: 12, fontSize: 12, color: T.fgMuted }}>Loading…</div>
                )}
                {clients !== null && siblings.length === 0 && others.length === 0 && (
                  <div style={{ padding: 12, fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>
                    No clients match.
                  </div>
                )}

                {/* This business's locations first */}
                {siblings.length > 0 && (
                  <>
                    <div style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: T.cyan,
                      padding: '8px 10px 4px',
                    }}>
                      {isMultiLocation
                        ? `🏢 ${headClient.name} · ${siblings.length} locations`
                        : 'Current client'}
                    </div>
                    {siblings.map(c => (
                      <ClientRow key={c.id} c={c} current={c.id === currentClient.id}
                        onClick={() => { setOpen(false); navigate(`/admin/clients/${c.id}`); }}
                        branch={isMultiLocation} />
                    ))}
                    {others.length > 0 && (
                      <>
                        <div style={{ borderTop: `1px solid ${T.border}`, margin: '6px 4px' }} />
                        <div style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                          textTransform: 'uppercase', color: T.fgMuted,
                          padding: '4px 10px',
                        }}>Other businesses</div>
                      </>
                    )}
                  </>
                )}

                {others.map(c => (
                  <ClientRow key={c.id} c={c} current={c.id === currentClient.id}
                    onClick={() => { setOpen(false); navigate(`/admin/clients/${c.id}`); }} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {!isClientRole && (
            <GBIBtn variant="primary" size="sm" onClick={handleRunAll} disabled={running}>
              {running ? (isMobile ? '⏳' : '⏳ Updating…') : (isMobile ? '↻ Update' : '↻ Update client data')}
            </GBIBtn>
          )}
          {user?.role === 'admin' && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Permanently delete this client"
              aria-label="Delete this client"
              style={{
                background: 'transparent', border: `1px solid ${T.magenta}`,
                color: T.magenta, borderRadius: 6, padding: isMobile ? '6px 8px' : '6px 10px',
                cursor: deleting ? 'wait' : 'pointer',
                fontSize: 11, fontWeight: 600,
                opacity: deleting ? 0.5 : 1,
              }}
            >
              {deleting ? (isMobile ? '…' : 'Deleting…') : (isMobile ? '🗑' : '🗑 Delete')}
            </button>
          )}
        </div>
      </div>

      {deleteError && (
        <div style={{
          position: 'fixed', top: 70, right: 20, zIndex: 2100,
          background: '#E8501A', color: '#fff', padding: '10px 14px',
          borderRadius: 8, fontSize: 12, maxWidth: 360,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
          onClick={() => setDeleteError(null)}
        >
          <strong>Delete failed.</strong>
          <div style={{ fontSize: 11, marginTop: 4, fontFamily: sharedStyles.fontMono, opacity: 0.9 }}>{deleteError}</div>
        </div>
      )}

      {(runResult || runError) && (
        <RunAllOverlay
          steps={runResult}
          error={runError}
          onClose={() => { setRunResult(null); setRunError(null); }}
        />
      )}
    </>
  );
}

// One row in the client switcher dropdown.
function ClientRow({ c, current, onClick, branch }: {
  c: Client; current: boolean; onClick: () => void; branch?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 10px', borderRadius: 4, cursor: 'pointer',
        background: current ? 'rgba(18,181,196,0.10)' : 'transparent',
        marginLeft: branch ? 8 : 0,
        borderLeft: branch ? `2px solid ${T.cyan}` : '2px solid transparent',
      }}
      onMouseEnter={e => { if (!current) e.currentTarget.style.background = T.bgSubtle; }}
      onMouseLeave={e => { if (!current) e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: T.fgPrimary }}>
        {branch ? (c.locationLabel?.trim() || c.location) : c.name}
        {current && <span style={{ fontSize: 9, color: T.cyan, marginLeft: 6 }}>● current</span>}
      </div>
      <div style={{ fontSize: 10, color: T.fgMuted, marginTop: 2 }}>
        📍 {c.location}
      </div>
    </div>
  );
}

// ── Run-All result overlay ─────────────────────────────────────────────────

function RunAllOverlay({ steps, error, onClose }: {
  steps: RunAllStep[] | null;
  error: string | null;
  onClose: () => void;
}) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 540, maxWidth: '92vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          fontFamily: sharedStyles.fontDisplay, fontSize: 20, fontWeight: 700,
          color: T.fgPrimary, marginBottom: 14,
        }}>Update — Results</div>

        {error && (
          <div style={{
            padding: '12px 14px',
            background: 'rgba(232,80,26,0.08)', border: '1px solid #E8501A',
            borderRadius: 6, fontSize: 13, color: '#C03B0F',
          }}>
            <strong>Failed before completion:</strong>
            <div style={{ marginTop: 4, fontFamily: sharedStyles.fontMono, fontSize: 11 }}>{error}</div>
          </div>
        )}

        {steps && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {steps.map((s, i) => <StepRow key={i} step={s} />)}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <GBIBtn variant="primary" onClick={onClose}>Done</GBIBtn>
        </div>
      </div>
    </div>
  );
}

const STEP_LABEL: Record<RunAllStep['step'], string> = {
  place:         '📍 Google Places (NAP)',
  lf_maps:       '🗺️ Local Falcon Maps',
  ai_visibility: '🤖 AI Visibility',
  insights:      '💡 AI Insights (Gemini)',
};

const STATUS_COLOR: Record<RunAllStep['status'], string> = {
  ok:      '#188038',
  skipped: '#9aa0a6',
  failed:  '#C03B0F',
};

const STATUS_ICON: Record<RunAllStep['status'], string> = {
  ok:      '✓',
  skipped: '○',
  failed:  '✕',
};

function StepRow({ step }: { step: RunAllStep }) {
  const color = STATUS_COLOR[step.status];
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '10px 14px',
      background: T.bgSubtle, border: `1px solid ${T.border}`, borderRadius: 6,
    }}>
      <div style={{
        width: 22, height: 22, borderRadius: 11,
        background: color, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, flexShrink: 0,
      }}>{STATUS_ICON[step.status]}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: sharedStyles.fontDisplay, fontSize: 13, fontWeight: 600,
          color: T.fgPrimary,
        }}>{STEP_LABEL[step.step]}</div>
        {step.message && (
          <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{step.message}</div>
        )}
        {step.details !== undefined && step.details !== null && (
          <div style={{
            marginTop: 4, fontSize: 10, fontFamily: sharedStyles.fontMono,
            color: T.fgSecondary,
          }}>
            {JSON.stringify(step.details, null, 0).replace(/[{}"]/g, '').replace(/,/g, ' · ')}
          </div>
        )}
      </div>
    </div>
  );
}
